import { create } from 'zustand'
import { api } from '@/lib/api'

// ===================
// Types
// ===================

export type MyWorkRole = 'writer' | 'reviewer'

export type MyWorkCollaborationStatus =
  | 'not_started'
  | 'in_progress'
  | 'draft_complete'
  | 'in_review'
  | 'changes_requested'
  | 'approved'
  | 'complete'

export interface MyWorkItem {
  id: string
  organizationId: string
  bidId: string
  bidTitle: string
  artifactId: string
  artifactName: string
  sectionKey: string
  sectionTitle: string
  role: MyWorkRole
  collaborationStatus: MyWorkCollaborationStatus
  reviewerName: string | null
  /**
   * Drafted words in the section (null when nothing is drafted). Optional
   * because an older backend omits it; the "N of M drafted" count then falls
   * back to status alone (see `sectionProgressBucket`).
   */
  wordCount?: number | null
  createdAt: string
}

/**
 * A comment thread waiting on the user: someone @mentioned them, or replied to
 * a comment they started, after their own last post in it. Open threads only —
 * the server drops a thread once it is resolved or the user has replied.
 */
export interface MyWorkCommentItem {
  commentId: string
  reason: 'mention' | 'reply'
  bidId: string
  bidTitle: string
  artifactId: string
  artifactName: string
  /** Null for a whole-document comment. */
  sectionTitle: string | null
  snippet: string
  actorName: string
  at: string
  /** Deep link into the editor, focused on the comment. */
  link: string
}

interface MyWorkListResponse {
  items: MyWorkItem[]
  /** Null when the server could not load them; absent from an older backend. */
  comments?: MyWorkCommentItem[] | null
}

// ===================
// Request de-duplication
// ===================

// Module-scope, like bids-store's guard, so it survives StrictMode remounts.
// The sidebar count and Home / My Work all read this list. Without the guard
// the sidebar would refetch on every navigation, and Home + sidebar mounting
// together would issue two identical requests.
//
// Cleared by reset() on sign-out so user B never adopts user A's request.
let inFlightMyWork: Promise<void> | null = null
let myWorkLoaded = false
// Bumped by reset() so a response already in flight at sign-out cannot write
// the previous user's items into the fresh store.
let myWorkRequestId = 0

// ===================
// Store
// ===================

interface MyWorkState {
  items: MyWorkItem[]
  comments: MyWorkCommentItem[]
  /** The comments list failed to load (the assignments may still be fine). */
  commentsError: boolean
  isLoading: boolean
  error: string | null

  /** Fetch the list. A call made while a request is already in flight joins it. */
  fetchMyWork: () => Promise<void>
  /** Fetch only if the list has never loaded (e.g. the sidebar count). */
  ensureMyWork: () => Promise<void>
  /** Clears the module-scope de-duplication state (called on sign-out). */
  reset: () => void
}

export const useMyWorkStore = create<MyWorkState>()((set, get) => ({
  items: [],
  comments: [],
  commentsError: false,
  isLoading: false,
  error: null,

  fetchMyWork: () => {
    if (inFlightMyWork) return inFlightMyWork

    const requestId = ++myWorkRequestId
    const request: Promise<void> = (async () => {
      set({ isLoading: true, error: null })
      try {
        const data = await api.get<MyWorkListResponse>('/my-work')
        if (requestId !== myWorkRequestId) return
        myWorkLoaded = true
        set({
          items: data.items,
          comments: data.comments ?? [],
          commentsError: data.comments === null,
          isLoading: false,
        })
      } catch {
        if (requestId !== myWorkRequestId) return
        set({ isLoading: false, error: 'Failed to load work items' })
      }
    })().finally(() => {
      // Only retract our own promise — after a sign-out the next user's
      // request may already be stored here.
      if (inFlightMyWork === request) inFlightMyWork = null
    })
    inFlightMyWork = request
    return request
  },

  ensureMyWork: async () => {
    if (myWorkLoaded) return
    return get().fetchMyWork()
  },

  reset: () => {
    myWorkRequestId++
    inFlightMyWork = null
    myWorkLoaded = false
  },
}))
